/**
 * The operating-pattern grid's data model, as pure functions.
 *
 * A location's roster shape is a set of CELLS, one per (weekday, shift type):
 *
 *     "0::sb1" -> { lead: {min, target, maxRank},
 *                   support: {min, target, [minRank], [maxRank], [slots]} }
 *
 * The support rank band (minRank..maxRank, 0 = most senior) is optional and
 * present only when set: a department that never uses it saves exactly what it
 * always saved. `slots` (Oct 2026) gives each support POSITION its own band,
 * [{minRank, maxRank}, ...] -- e.g. main's "special" OT session: one Senior
 * Resident and one MO/Resident. Absent = every position uses the cell's band.
 *
 * Two screens edit this: the clinic-creation modal and the clinic drawer. They
 * used to be different things entirely -- creation offered a plain checkbox
 * grid keyed by START TIME (so MID and SB, both 09:00-18:00, were one column)
 * and wrote the generation-1 `location_schedules` field, which nothing rosters
 * from. The result was a new clinic that had to be configured a second time,
 * from scratch, in the drawer.
 *
 * Keeping the model here means both screens agree by construction rather than
 * by two people remembering to make the same change twice.
 *
 * Keyed by shift type ID throughout, never by time or short code: ids are the
 * only thing guaranteed unique.
 */

// Mon=0 .. Sun=6, matching Python's weekday().
export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday',
                     'Saturday', 'Sunday'];

/** Shift-type ids may contain underscores, so the day is split off the FRONT only. */
export const cellKey = (day, typeId) => `${day}::${typeId}`;

export const parseCellKey = (key) => {
  const i = String(key).indexOf('::');
  return { day: Number(String(key).slice(0, i)), typeId: String(key).slice(i + 2) };
};

export const pick = (obj, ...keys) => {
  for (const k of keys) if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
  return undefined;
};

export const shortCodeOf = (shiftType) =>
  String(pick(shiftType || {}, 'shortCode', 'short_code') || shiftType?.id || '').toUpperCase();

/** A newly switched-on cell: one lead, no support, no rank ceiling. */
const emptyCell = () => ({
  lead: { min: 1, target: 1, maxRank: null },
  support: { min: 0, target: 0 },
});

export function toggleCell(cells, day, typeId) {
  const key = cellKey(day, typeId);
  const next = { ...(cells || {}) };
  if (next[key]) delete next[key];
  else next[key] = emptyCell();
  return next;
}

/**
 * Set one count. `group` is 'lead' | 'support', `field` is 'min' | 'target'.
 *
 * Lead is floored at 1 because a session that runs at all needs somebody in
 * charge -- the API and generation both enforce it, so allowing 0 here would
 * only show a number that is not what gets saved. min and target are kept
 * consistent with each other in both directions.
 */
export function setCellCount(cells, day, typeId, group, field, raw) {
  const key = cellKey(day, typeId);
  if (!cells?.[key]) return cells;
  const floor = group === 'lead' ? 1 : 0;
  const value = Math.max(floor, Math.min(99, parseInt(raw, 10) || floor));
  const g = { ...cells[key][group], [field]: value };
  if (field === 'target' && g.min > value) g.min = value;
  if (field === 'min' && value > g.target) g.target = value;
  return { ...cells, [key]: { ...cells[key], [group]: g } };
}

/** Seniority ceiling for the lead slot. '' clears it (any rank may lead). */
export function setCellLeadRank(cells, day, typeId, raw) {
  const key = cellKey(day, typeId);
  if (!cells?.[key]) return cells;
  const value = raw === '' || raw === null || raw === undefined
    ? null
    : Math.max(0, Math.min(4, parseInt(raw, 10)));
  return { ...cells, [key]: { ...cells[key], lead: { ...cells[key].lead, maxRank: value } } };
}

/**
 * Rank band for the support slots. `end` is 'minRank' (most senior allowed) or
 * 'maxRank' (least senior allowed). '' clears that end (open). The key is
 * removed rather than stored as null, so an unbanded cell stays {min, target}.
 */
export function setCellSupportRank(cells, day, typeId, end, raw) {
  const key = cellKey(day, typeId);
  if (!cells?.[key] || (end !== 'minRank' && end !== 'maxRank')) return cells;
  const support = { ...cells[key].support };
  if (raw === '' || raw === null || raw === undefined) delete support[end];
  else support[end] = Math.max(0, Math.min(4, parseInt(raw, 10)));
  return { ...cells, [key]: { ...cells[key], support } };
}

/**
 * Give each support position its own band (on), or go back to one band for
 * the cell (off). Switching on starts every position at the cell's band.
 */
export function setCellPerPosition(cells, day, typeId, on) {
  const key = cellKey(day, typeId);
  if (!cells?.[key]) return cells;
  const support = { ...cells[key].support };
  if (on) {
    const band = {};
    if (support.minRank !== undefined) band.minRank = support.minRank;
    if (support.maxRank !== undefined) band.maxRank = support.maxRank;
    support.slots = Array.from({ length: Math.max(0, support.target || 0) }, () => ({ ...band }));
  } else {
    delete support.slots;
  }
  return { ...cells, [key]: { ...cells[key], support } };
}

/** One support position's band: `position` 0-based, `end` 'minRank' | 'maxRank'. */
export function setCellSlotRank(cells, day, typeId, position, end, raw) {
  const key = cellKey(day, typeId);
  if (!cells?.[key] || (end !== 'minRank' && end !== 'maxRank')) return cells;
  const support = { ...cells[key].support };
  const slots = [...(support.slots || [])];
  while (slots.length <= position) slots.push({});
  const band = { ...slots[position] };
  if (raw === '' || raw === null || raw === undefined) delete band[end];
  else band[end] = Math.max(0, Math.min(4, parseInt(raw, 10)));
  slots[position] = band;
  support.slots = slots;
  return { ...cells, [key]: { ...cells[key], support } };
}

/**
 * Warnings for the grid.
 *
 * This once read `cell.target`, a field the cell shape has not had for some
 * time, so every cell warned "target is 0" permanently -- seven warnings on a
 * seven-day ward that no configuration could clear, which teaches the operator
 * to ignore the panel. A warning that is always on carries no information.
 */
export function computePatternWarnings(cells, shiftTypes = []) {
  const out = [];
  Object.entries(cells || {}).forEach(([key, cell]) => {
    const { day, typeId } = parseCellKey(key);
    const code = shortCodeOf(shiftTypes.find(t => String(t.id) === typeId)) || typeId.toUpperCase();
    const label = `${DAYS[day]?.slice(0, 3) || day}/${code}`;

    const leadTarget = Number(cell?.lead?.target ?? 0);
    const supportTarget = Number(cell?.support?.target ?? 0);

    const sMin = cell?.support?.minRank;
    const sMax = cell?.support?.maxRank;
    if (sMin !== undefined && sMax !== undefined && sMin > sMax) {
      out.push(`${label}: support rank band is empty — the most senior rank allowed `
               + `is junior to the least senior allowed, so nobody can fill support`);
    }
    (cell?.support?.slots || []).slice(0, supportTarget).forEach((b, i) => {
      if (b?.minRank !== undefined && b?.maxRank !== undefined && b.minRank > b.maxRank) {
        out.push(`${label}: support position ${i + 1}'s rank band is empty, so nobody can fill it`);
      }
    });

    if (leadTarget + supportTarget <= 0) {
      out.push(`${label}: target is 0, nothing will be rostered`);
    } else if (leadTarget < 1) {
      out.push(`${label}: a session always has at least 1 lead — saving this `
               + `will store 1, not ${leadTarget}. Remove the day from the `
               + `pattern to close the session instead.`);
    }
  });
  return out;
}

/**
 * Build the two API payloads a pattern save needs.
 *
 * They go to separate endpoints and ORDER MATTERS: writing the pattern triggers
 * _reconcile_staffing_requirements_for_location(), which defaults every target
 * to 1 (the pattern's own required_staff does not propagate). Staffing must be
 * written SECOND or the operator's real numbers are overwritten by defaults.
 */
export function cellsToPayload(cells) {
  const entries = Object.entries(cells || {});

  const pattern = entries.map(([key, cell]) => {
    const { day, typeId } = parseCellKey(key);
    const total = Math.max(1, (cell.lead?.target || 1) + (cell.support?.target || 0));
    return { day, shift_type_id: typeId, required_staff: total };
  });

  const requirements = entries.map(([key, cell]) => {
    const { day, typeId } = parseCellKey(key);
    const lead = cell.lead || { min: 1, target: 1, maxRank: null };
    const support = cell.support || { min: 0, target: 0 };
    return {
      day,
      shift_type_id: typeId,
      // Derived server-side too, but sent so a legacy reader sees consistent
      // numbers rather than a row whose parts and totals disagree.
      total_min_required: Math.max(1, lead.min || 1) + Math.max(0, support.min || 0),
      total_target_required: Math.max(1, lead.target || 1) + Math.max(0, support.target || 0),
      lead: {
        min: Math.max(1, lead.min || 1),
        target: Math.max(1, lead.target || 1),
        max_rank: lead.maxRank ?? null,
      },
      support: {
        min: Math.max(0, support.min || 0),
        target: Math.max(0, support.target || 0),
        // Optional rank band, sent only when set.
        ...(support.maxRank !== undefined && support.maxRank !== null ? { max_rank: support.maxRank } : {}),
        ...(support.minRank !== undefined && support.minRank !== null ? { min_rank: support.minRank } : {}),
        // Round-tripped, not edited here: a department whose support slots do
        // not need the location's skills (set by the data converter) must not
        // lose that setting when a planner saves the pattern.
        ...(support.requiresLocationSkills === false ? { requires_location_skills: false } : {}),
        // Per-position bands, one per support position actually rostered.
        ...(Array.isArray(support.slots) && support.slots.length
          ? { slots: support.slots.slice(0, Math.max(0, support.target || 0)).map(b => ({
              ...(b?.minRank !== undefined && b?.minRank !== null ? { min_rank: b.minRank } : {}),
              ...(b?.maxRank !== undefined && b?.maxRank !== null ? { max_rank: b.maxRank } : {}),
            })) }
          : {}),
      },
    };
  });

  return { pattern, requirements };
}

/** Copy an optional support rank band from an API row onto a cell's support. */
const supportBand = (support, raw) => {
  const out = { ...support };
  const maxRank = pick(raw || {}, 'maxRank', 'max_rank');
  const minRank = pick(raw || {}, 'minRank', 'min_rank');
  if (maxRank !== undefined) out.maxRank = Number(maxRank);
  if (minRank !== undefined) out.minRank = Number(minRank);
  if (pick(raw || {}, 'requiresLocationSkills', 'requires_location_skills') === false) {
    out.requiresLocationSkills = false;
  }
  const slots = pick(raw || {}, 'slots');
  if (Array.isArray(slots) && slots.length) {
    out.slots = slots.map(b => {
      const band = {};
      const lo = pick(b || {}, 'minRank', 'min_rank');
      const hi = pick(b || {}, 'maxRank', 'max_rank');
      if (lo !== undefined) band.minRank = Number(lo);
      if (hi !== undefined) band.maxRank = Number(hi);
      return band;
    });
  }
  return out;
};

/** Rebuild cells from what the API returns, for the edit screen. */
export function payloadToCells(pattern = [], requirements = []) {
  const rowFor = {};
  (requirements || []).forEach(r => {
    const day = Number(pick(r, 'day') ?? -1);
    const typeId = String(pick(r, 'shiftTypeId', 'shift_type_id') ?? '');
    if (typeId) rowFor[cellKey(day, typeId)] = r;
  });

  const cells = {};
  (pattern || []).forEach(item => {
    const day = Number(pick(item, 'day') ?? -1);
    const typeId = String(pick(item, 'shiftTypeId', 'shift_type_id') ?? '');
    if (!typeId || day < 0 || day > 6) return;

    const key = cellKey(day, typeId);
    const row = rowFor[key];
    if (!row) { cells[key] = emptyCell(); return; }

    const leadRaw = pick(row, 'lead');
    const supportRaw = pick(row, 'support');
    if (leadRaw || supportRaw) {
      const maxRank = pick(leadRaw || {}, 'maxRank', 'max_rank');
      cells[key] = {
        lead: {
          min: Math.max(1, Number(pick(leadRaw || {}, 'min') ?? 1)),
          target: Math.max(1, Number(pick(leadRaw || {}, 'target') ?? 1)),
          maxRank: maxRank === undefined || maxRank === null ? null : Number(maxRank),
        },
        support: supportBand({
          min: Math.max(0, Number(pick(supportRaw || {}, 'min') ?? 0)),
          target: Math.max(0, Number(pick(supportRaw || {}, 'target') ?? 0)),
        }, supportRaw),
      };
      return;
    }

    // Legacy row: totals only, no lead/support split. One lead, the rest support.
    const tMin = Number(pick(row, 'totalMinRequired', 'total_min_required') ?? 1);
    const tTarget = Number(pick(row, 'totalTargetRequired', 'total_target_required') ?? 1);
    cells[key] = {
      lead: { min: 1, target: 1, maxRank: null },
      support: { min: Math.max(0, tMin - 1), target: Math.max(0, tTarget - 1) },
    };
  });
  return cells;
}
